import { HttpParams } from '@angular/common/http';
import {
  Component,
  ElementRef,
  Input,
  NgZone,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { Project } from 'src/app/interfaces/project';
import { LocationService } from 'src/app/services/location.service';
import { ProjectService } from 'src/app/services/project.service';
import * as mapboxgl from 'mapbox-gl';
import { Point } from 'geojson';
import {
  FormBuilder,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { debounceTime } from 'rxjs/operators';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { Subscription } from 'rxjs';
import { WhiteLabelApplicationService } from 'src/app/services/white-label-application.service';
import { DeviceService } from '../../services/device.service';
import { SearchItemComponent } from 'src/app/components/search-item/search-item.component';
import { KeyboardService } from 'src/app/services/keyboard.service';
import { ApiInit } from 'src/app/utils/api-init';
import { Hydra } from 'src/app/interfaces/hydra';
import { MapboxService } from 'src/app/services/mapbox.service';
import { AnalyticsType } from 'src/app/enums/analytics-type';
import { TranslateModule } from '@ngx-translate/core';
import { SearchEmptyComponent } from 'src/app/components/search-empty/search-empty.component';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { LoaderComponent } from 'src/app/components/loader/loader.component';
import { NgIf, NgFor } from '@angular/common';
import { InlineSVGModule } from 'ng-inline-svg-2';
import {
  IonBackButton,
  IonContent,
  IonFooter,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
} from '@ionic/angular/standalone';
import { MapComponent } from 'src/app/components/map/map.component';
import { ToastService } from 'src/app/services/toast.service';
import { DivClickDirective } from 'src/app/directives/div-click.directive';
import { Router, RouterLink } from '@angular/router';

@Component({
  selector: 'app-search',
  templateUrl: './search.page.html',
  standalone: true,
  imports: [
    FormsModule,
    MapComponent,
    ReactiveFormsModule,
    InlineSVGModule,
    NgIf,
    LoaderComponent,
    NgFor,
    SearchItemComponent,
    NgxSkeletonLoaderModule,
    SearchEmptyComponent,
    TranslateModule,
    IonContent,
    IonInfiniteScroll,
    IonInfiniteScrollContent,
    DivClickDirective,
    IonBackButton,
    IonFooter,
    RouterLink,
  ],
})
export class SearchPage implements OnInit {
  @ViewChildren(SearchItemComponent)
  searchItemElements: QueryList<SearchItemComponent>;
  @ViewChild('searchResultElement') searchResultsElement: ElementRef;
  @ViewChild(IonInfiniteScroll) infiniteScroll: IonInfiniteScroll;
  @ViewChild(IonContent) content: IonContent;

  mapError: boolean = false;
  location: number[];
  locationAccuracy: number;
  hasUserLocation: boolean = false;
  bounds: mapboxgl.LngLatBounds;
  markers = {};
  displayedMarkers = {};
  isSearchVisible: boolean = false;
  isOnboarding: boolean = false;
  projects: Project[] = [];
  mapMarkers: Project[] = [];
  searchResults: Project[] = [];
  searchControl: FormControl;
  mapFinishedLoading: boolean = false;
  projectsLoaded: boolean = false;
  deviceDataLoaded: boolean = false;
  searching: boolean = false;
  currentFilter: string = 'distance';
  starSubscription: Subscription;
  page: number = 1;
  perPage: number = 7;
  totalItems: number;
  map: mapboxgl.Map;
  public allProjectsCount: number = 0;
  private selectedMarkerIndex: Number;

  constructor(
    private locationService: LocationService,
    private ngZone: NgZone,
    private projectService: ProjectService,
    private fb: FormBuilder,
    private whitelabelService: WhiteLabelApplicationService,
    private deviceService: DeviceService,
    private keyboardService: KeyboardService,
    private analyticsService: AnalyticsService,
    private apiInit: ApiInit,
    private mapboxService: MapboxService,
    private toastService: ToastService,
    private router: Router
  ) {
    if (this.router.url.includes('onboarding')) this.isOnboarding = true;
    this.createSearchControl();
  }

  ngOnInit() {
    if (!this.mapboxService.isWebGLSupported()) {
      this.mapError = true;
      this.mapFinishedLoading = true;
      this.searching = false;
      this.totalItems = 0;
      this.projectsLoaded = true;
    }

    this.starSubscription = this.projectService.starChanged$.subscribe(
      (project) => {
        const starredProject =
          this.projects.length > 0
            ? this.projects.find((_project) => project.id == _project.id)
            : undefined;
        if (starredProject) {
          starredProject.starred = project.starred;
        }
        const foundProject =
          this.searchResults.length > 0
            ? this.searchResults.find((_project) => project.id == _project.id)
            : undefined;
        if (foundProject) {
          foundProject.starred = project.starred;
        }
      }
    );

    this.keyboardService.registerWillHideListener(() => {
      const tabBar = document.querySelector('ion-tab-bar');
      if (tabBar) {
        tabBar.classList.remove('tab-bar-hidden');
        tabBar.removeAttribute('aria-hidden');
      }
    });

    this.apiInit.watch(async () => {
      try {
        this.allProjectsCount =
          await this.projectService.getNumberOfAllProjects();
      } catch (error) {
        // ignore
      }
    });
  }

  ngOnDestroy() {
    this.starSubscription.unsubscribe();
  }

  createSearchControl() {
    this.searchControl = this.fb.control(null);

    this.searchControl.valueChanges.subscribe(() => {
      this.searching = true;
      this.deviceDataLoaded = false;
      this.searchResults = [];
    });

    this.searchControl.valueChanges
      .pipe(debounceTime(500))
      .subscribe(async (value) => {
        this.doSearch(value);
      });
  }

  async loadList() {
    let projectResponse: Hydra<Project>;

    try {
      projectResponse = await this.projectService.getProjectsByLocationBox(
        this.setUrlParams()
      );
    } catch (error) {
      return;
    }

    this.ngZone.run(() => {
      this.projects = projectResponse.member;
      this.totalItems = projectResponse.totalItems;
      this.processProjects(this.projects);
      this.projectsLoaded = true;
      this.searching = false;
    });
  }

  async locateUser() {
    if (!this.hasUserLocation) {
      try {
        const location = await this.locationService.getCurrentPosition();
        this.location = [location.coords.latitude, location.coords.longitude];
        this.locationAccuracy = location.coords.accuracy;
        this.mapboxService.drawLocationCircle(
          this.map,
          this.location,
          this.locationAccuracy
        );
        this.hasUserLocation = true;
      } catch (error) {
        this.toastService.show('address.create.location-error');
        return;
      }
    }

    this.map.flyTo({
      center: [this.location[1], this.location[0]],
      zoom: 12,
    });
  }

  async loadResults() {
    this.content.scrollToTop();
    try {
      this.page = 1;
      let params = new HttpParams();
      if (this.map.getZoom() <= 6.5) {
        params = params.set('zoom', '1');
      } else {
        params = this.setUrlParams();
      }
      params = params.set(
        'wl',
        this.whitelabelService.currentWhiteLabelApp.id.toString()
      );
      const mapData = await this.projectService.getProjectsByLocation(params);
      this.ngZone.run(() => {
        this.mapMarkers = mapData;

        if (!this.bounds) {
          this.mapboxService.fitBoundForProjects(this.map, this.mapMarkers);
        }

        (this.map.getSource('projects') as mapboxgl.GeoJSONSource).setData(
          this.mapboxService.buildGeoJSON(this.mapMarkers)
        );

        // Wait until map is idle to make sure the clusters have been recalculated
        // See: https://github.com/mapbox/mapbox-gl-js/issues/8478
        this.map.once('idle', () => {
          this.updateMarkers();
        });
      });
    } catch (error) {
    } finally {
      this.mapFinishedLoading = true;
    }
  }

  async processProjects(projects: Project[]) {
    let projectIds = projects.map(({ id }) => id);

    try {
      const params = new HttpParams().set('project_ids', projectIds.join(','));
      const response = this.location
        ? await this.deviceService.getData(
            params,
            new mapboxgl.LngLat(this.location[1], this.location[0])
          )
        : await this.deviceService.getData(params);

      projects = projects.map((project) => {
        project.starred = response.projects[project.id].starred;
        project.distance = response.projects[project.id].distance;
        return project;
      });
      this.deviceDataLoaded = true;
    } catch (error) {}
  }

  async doSearch(keyword) {
    try {
      const response = await this.projectService.getProjectsByKeyword(
        this.setUrlParams(keyword)
      );
      this.searchResults = response;
      this.searching = false;
      this.processProjects(this.searchResults);
    } catch (error) {
      console.log(error);
    }
    this.analyticsService.logEvent(AnalyticsType.SEARCH, {
      search_term: keyword,
    });
  }

  isActive(filter: string): boolean {
    return this.currentFilter === filter;
  }

  switchFilter(filter: string) {
    if (this.currentFilter === filter) {
      return;
    }

    this.searching = true;
    this.deviceDataLoaded = false;
    this.searchResults = [];

    const old = this.currentFilter;
    this.currentFilter = filter;

    this.doSearch(this.searchControl.value);

    this.analyticsService.logEvent(AnalyticsType.SWITCH_VIEW, {
      view_new: filter,
      view_old: old,
    });
  }

  initMarkers() {
    this.map.addSource('projects', {
      type: 'geojson',
      data: this.mapboxService.buildGeoJSON(this.mapMarkers),
      cluster: true,
      clusterMaxZoom: 14,
      clusterRadius: 60,
    });

    this.map.addLayer({
      id: 'clusters',
      type: 'circle',
      source: 'projects',
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': '#ffffff',
        'circle-radius': ['step', ['get', 'point_count'], 20, 100, 30, 750, 40],
      },
    });

    this.map.on('click', 'clusters', (e) => {
      const features = this.map.queryRenderedFeatures(e.point, {
        layers: ['clusters'],
      });
      const clusterId = features[0].properties.cluster_id;
      (this.map.getSource('projects') as any).getClusterExpansionZoom(
        clusterId,
        (err, zoom) => {
          if (zoom) {
            this.map.easeTo({
              center: (features[0].geometry as any).coordinates,
              zoom,
            });
          }
        }
      );
    });

    this.map.addLayer({
      id: 'cluster-count',
      type: 'symbol',
      source: 'projects',
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-font': ['Open Sans Regular'],
        'text-size': 12,
      },
    });
  }

  private createMarker(
    coords: mapboxgl.LngLatLike,
    id: any,
    slug: string
  ): mapboxgl.Marker {
    const el = document.createElement('div');
    el.className = 'marker';
    el.id = `marker-${id}`;

    let img =
      this.whitelabelService.currentWhiteLabelApp?.appIconThumbnails?.small;
    if (img) {
      let icon = document.createElement('img');
      icon.className = 'icon';
      icon.setAttribute('src', img);
      icon.setAttribute('alt', slug);
      el.appendChild(icon);
    }

    const opts: mapboxgl.MarkerOptions = { anchor: 'bottom' };

    let marker = new mapboxgl.Marker(el, opts);
    marker.setLngLat(coords);
    this.markers[id] = marker;
    el.addEventListener('click', async () => {
      let elem = document.querySelector(
        '.app__content #project-' + CSS.escape(slug)
      );
      if (elem === null) {
        try {
          const project = await this.projectService.getProject(slug);
          this.processProjects([project]);
          this.projects.unshift(project);
        } catch (error) {
          // ignore
        }
      }
      this.ngZone.run(() => {
        this.waitForElementToExist(
          `.search__top #marker-${this.selectedMarkerIndex}`
        ).then((element) => {
          if (this.selectedMarkerIndex) {
            const previousTransform = element.style.transform;
            const newTransform = previousTransform.replace(
              /scale\([^)]*\)/,
              ''
            );
            element.style.transform = newTransform;
          }
        });
        const currentTransform = window.getComputedStyle(el).transform;
        el.style.transform = `${currentTransform} scale(1.2)`;
        this.selectedMarkerIndex = id;

        this.waitForElementToExist(
          '.app__content #project-' + CSS.escape(slug)
        ).then((element) => {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'start',
          });
          element.classList.add('highlighted');

          setTimeout(() => {
            element.classList.remove('highlighted');
          }, 1500);
        });
      });
    });

    return marker;
  }

  waitForElementToExist(selector: any): Promise<HTMLElement> {
    return new Promise((resolve) => {
      if (document.querySelector(selector)) {
        return resolve(document.querySelector(selector));
      }

      const observer = new MutationObserver(() => {
        if (document.querySelector(selector)) {
          resolve(document.querySelector(selector));
          observer.disconnect();
        }
      });

      observer.observe(document.body, {
        subtree: true,
        attributes: false,
        childList: true,
      });
    });
  }

  async loadData(event) {
    this.page++;

    let projectResponse = [];
    try {
      let response = await this.projectService.getProjectsByLocationBox(
        this.setUrlParams()
      );
      projectResponse = response.member;
    } catch (error) {
      this.page--;
      event.target.complete();
      return;
    }

    projectResponse = projectResponse.filter((element) => {
      if (this.projects.find((s) => s.id == element.id)) {
        this.totalItems--;
        return false;
      }
      return true;
    });
    this.processProjects(projectResponse);
    this.projects = this.projects.concat(projectResponse);
    event.target.complete();
  }

  private updateMarkers() {
    let features = this.map.querySourceFeatures('projects');
    let newDisplayedMarkers = {};

    for (const feature of features) {
      const props = feature.properties;
      if (props.cluster) continue;
      let marker = this.markers[props.id];

      if (!marker) {
        const coords = (feature.geometry as Point)
          .coordinates as mapboxgl.LngLatLike;
        marker = this.createMarker(coords, props.id, props.slug);
      }

      if (!this.displayedMarkers[props.id]) {
        marker.addTo(this.map);
      }

      newDisplayedMarkers[props.id] = marker;
    }

    for (const id in this.displayedMarkers) {
      if (!newDisplayedMarkers[id]) {
        this.displayedMarkers[id].remove();
      }
    }
    this.displayedMarkers = newDisplayedMarkers;
  }

  async mapLoaded(map: mapboxgl.Map) {
    this.map = map;

    try {
      const location = await this.locationService.getCurrentPosition();
      this.location = [location.coords.latitude, location.coords.longitude];
      this.locationAccuracy = location.coords.accuracy;
    } catch (error) {
      this.currentFilter = 'recent';
    }

    if (this.location) {
      this.map.jumpTo({
        center: [this.location[1], this.location[0]],
        zoom: this.map.getZoom(),
      });
      this.hasUserLocation = true;
      this.mapboxService.drawLocationCircle(
        this.map,
        this.location,
        this.locationAccuracy
      );
    }

    this.initMapData();
    this.initMarkers();

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        this.map.resize({ skipBounds: true });
        observer.unobserve(this.map.getContainer());
        observer.disconnect();
      }
    });
    observer.observe(this.map.getContainer());
  }

  private initMapData() {
    this.ngZone.run(async () => {
      this.bounds = this.map.getBounds();
      this.loadResults();
      this.loadList();
    });

    this.map.on('movestart', (event) => {
      this.ngZone.run(() => {
        if (this.isSearchVisible) return;
        this.searching = true;
      });
    });

    this.map.on('moveend', async (event) => {
      this.ngZone.run(async () => {
        if (this.isSearchVisible) return;
        if ((<any>event).skipLoad) return;
        this.bounds = !(<any>event).skipBounds ? this.map.getBounds() : null;
        this.loadResults();
        this.loadList();
      });
    });
  }

  setUrlParams(keyword: string = null) {
    let params = new HttpParams();
    params = params
      .set('order', this.currentFilter)
      .set('direction', this.currentFilter == 'distance' ? 'asc' : 'desc');

    if (keyword !== null) {
      if (keyword !== '') params = params.set('keyword', keyword);
      if (this.location)
        params = params
          .set('location[lat]', this.location[0].toString())
          .set('location[lng]', this.location[1].toString());
      return params;
    }

    if (this.bounds) {
      params = params
        .set('box[0][lng]', this.bounds.getNorthEast().lng.toString())
        .set('box[0][lat]', this.bounds.getNorthEast().lat.toString())
        .set('box[1][lng]', this.bounds.getSouthWest().lng.toString())
        .set('box[1][lat]', this.bounds.getSouthWest().lat.toString());
    }

    params = params
      .set('page', this.page.toString())
      .set('perPage', this.perPage.toString());

    if (!this.location) {
      return params;
    }

    params = params
      .set('location[lat]', this.location[0].toString())
      .set('location[lng]', this.location[1].toString());
    return params;
  }

  startSearch() {
    this.isSearchVisible = true;
    if (!this.searchResults.length) {
      this.doSearch('');
    }
  }

  closeSearch() {
    this.isSearchVisible = false;
    this.searchControl.reset();
    setTimeout(() => {
      if (this.map) this.map.resize();
    });
  }

  hideKeyboard() {
    this.keyboardService.hideKeyboard();
  }
}
